import { RecordDataSource } from "../../../records/grids";
import {
  ColumnDefinitionSetService,
  vsServiceName as ColumnDefinitionSetServiceName,
  ColumnDefinitionSetServiceProvider,
  vsProviderName as ColumnDefinitionSetServiceProviderName,
} from "../../../records/grids/columns/columnDefinitionSetService";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../../records/grids/columns/columnDefinitionService";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../records/server/serverUtility";
import {
  GridConfiguration,
  RecordGridViewService,
  RecordGridViewServiceProvider,
  vsProviderName as RecordGridViewServiceProviderName,
  vsServiceName as RecordGridViewServiceName,
} from "../../../records/views/recordGridView";

import { machineObjectName as machineName } from "../../../machines/shared/names";
import { GridSelectionChangedArgs } from "../../../records/grids/controller";
import { IQuery, ServiceResponse } from "../../../records/server/query";
import {
  vsServiceName as ConfigurableGridFormViewServiceName,
  ConfigurableGridFormViewService,
} from "../../../records/views/configurableGridFormView/service";
import { Record } from "../../../records/server/crud";
import {
  vsComponentName as configurableGridFormViewComponentName,
  ConfigurableGridFormViewConfig,
} from "../../../records/views/configurableGridFormView";

import {
  GridSettingsService,
  vsName as GridSettingsServiceName,
  IGridSettings,
} from "../../../records/grids/settings/gridSettingsService";
import { ColDef } from "../../../records/grids/columns/colDef";
import { MachineDetailTabsProvider } from "../../../machines/shared/components/views/machines/detail/tabs";
import {
  ContractService,
  vsServiceName as ContractServiceName,
} from "../ContractService";
import { Machine } from "../../../machines/shared/models/machine";
import { MachineConfiguration } from "../../../machines/shared/models/machineConfiguration";
export const vsObjectName = "contractInstanceMachine";

export const vsMachineColumnSetName = `${vsObjectName}.machine`;
export const vsContractColumnSetName = `${vsObjectName}.contract`;

export function addContractTabToMachineDetail(config: {
  machineDetailsTabsProvider: MachineDetailTabsProvider;
  displayName: string;
  id: string;
  uiSrefFragment: string;
  contractTypeId: string;
  gridEditSymbolKey: string;
  gridViewSymbolKey: string;
  contractColumnSetName: string;
  contractMachineName: string;
}) {
  const {
    machineDetailsTabsProvider,
    displayName,
    id,
    uiSrefFragment,
    contractTypeId,
    gridEditSymbolKey,
    gridViewSymbolKey,
  } = config;

  machineDetailsTabsProvider.addElement({
    displayName,
    id,
    uiSrefFragment,
    requiredPermission: "contractView",
    stateDefinition: {
      url: `/${uiSrefFragment}`,
      component: configurableGridFormViewComponentName,
      resolve: {
        defaultQuery: [
          "machine",
          (machine: any) => ({ MachineId: machine.Id }),
        ],
        config: [
          RecordGridViewServiceName,
          GridSettingsServiceName,
          "machine",
          ColumnDefinitionSetServiceName,
          ColumnDefinitionServiceName,
          ContractServiceName,
          (
            recordGridViewService: RecordGridViewService,
            gridSettingsService: GridSettingsService,
            machine: Machine<MachineConfiguration>,
            columnDefinitionSetService: ColumnDefinitionSetService,
            columnDefinitionService: ColumnDefinitionService,
          ): ConfigurableGridFormViewConfig =>
            getConfigurableGridFormViewConfigForContractMachines(
              recordGridViewService,
              gridSettingsService.getGridSettings(Symbol(gridViewSymbolKey)),
              { MachineId: machine.Id },
              columnDefinitionSetService.getColumnDefinitionSet(
                config.contractColumnSetName,
              ),
              columnDefinitionService,
            ),
        ],
      },
    },
    children: [
      {
        displayName: "Edit Contracts",
        id: "edit",
        requiredPermission: "contractEdit",
        uiSrefFragment: "edit",
        stateDefinition: {
          url: "/edit",
          views: {
            "@^.^": {
              component: configurableGridFormViewComponentName,
            },
          },
          resolve: {
            selectedIds: [
              ServerUtilityName,
              RecordGridViewServiceName,
              "machine",
              (
                serverUtility: ServerUtility,
                recordGridViewService: RecordGridViewService,
                machine: Record,
              ) => {
                const gridConfig = recordGridViewService.getGridConfiguration(
                  config.contractMachineName,
                )!;
                return serverUtility
                  .fetchAll<Record>({
                    url: gridConfig.gridDataSource!.url,
                    query: { MachineId: machine.Id },
                  })
                  .then((result) => result.map((r) => r.ContractInstanceId));
              },
            ],
            gridConfiguration: [
              RecordGridViewServiceName,
              GridSettingsServiceName,
              (
                recordGridViewService: RecordGridViewService,
                gridSettingsService: GridSettingsService,
              ): GridConfiguration => {
                const config =
                  recordGridViewService.getGridConfiguration(contractTypeId)!;
                config.gridSettings = gridSettingsService.getGridSettings(
                  Symbol(gridEditSymbolKey),
                );
                return config;
              },
            ],
            config: [
              ConfigurableGridFormViewServiceName,
              ServerUtilityName,
              "selectedIds",
              "machine",
              "gridConfiguration",
              (
                configurableGridFormViewService: ConfigurableGridFormViewService,
                serverUtility: ServerUtility,
                selectedIds: number[],
                machine: Record,
                gridConfiguration: GridConfiguration,
              ): ConfigurableGridFormViewConfig =>
                getConfigurationGridEditViewConfigForContractMachines({
                  configurableGridFormViewService,
                  gridConfiguration,
                  selectedIds,
                  gridSelectionChanged: (args: GridSelectionChangedArgs) => {
                    const contractId = args.data.Id;
                    const machineId = machine.Id;
                    setGamingContractMachine(
                      serverUtility,
                      machineId,
                      contractId,
                      args.selected,
                    );
                  },
                }),
            ],
          },
        },
      },
    ],
  });
}

function setGamingContractMachine(
  serverUtility: ServerUtility,
  machineId: number,
  contractId: number,
  setSelection: boolean,
) {
  const url = "/api/contract/machines";

  setSelection
    ? serverUtility.post({
        url,
        postObject: {
          ContractInstanceId: contractId,
          MachineId: machineId,
        },
      })
    : serverUtility.sendApiRequest<ServiceResponse<Record>>({
        url: url + `/${contractId}/byMachineId`,
        method: "delete",
        data: {
          MachineId: machineId,
        },
      });
}

export function getContractMachineTab(config: {
  contractTypeId: string;
  machineColumnSetName: string;
  machineName: string;
  gridViewSymbolKey: string;
  gridEditSymbolKey: string;
  contractMachineObjectName: string;
}): any {
  return {
    name: "Machines",
    uiSref: "machines",
    state: {
      url: "/machines",
      views: {
        tabs: {
          component: configurableGridFormViewComponentName,
        },
      },
      resolve: {
        config: [
          "contract",
          RecordGridViewServiceName,
          GridSettingsServiceName,
          ColumnDefinitionSetServiceName,
          ColumnDefinitionServiceName,
          function (
            contract: Record,
            recordGridViewService: RecordGridViewService,
            gridSettingsService: GridSettingsService,
            columnDefinitionSetService: ColumnDefinitionSetService,
            columnDefinitionService: ColumnDefinitionService,
          ): ConfigurableGridFormViewConfig {
            return getConfigurableGridFormViewConfigForContractMachines(
              recordGridViewService,
              gridSettingsService.getGridSettings(
                Symbol(config.gridViewSymbolKey),
              ),
              { ContractInstanceId: contract.ContractInstanceId },

              columnDefinitionSetService.getColumnDefinitionSet(
                config.machineColumnSetName,
              ) ?? [],
              columnDefinitionService,
            );
          },
        ],
      },
    },
    children: [
      {
        name: "Edit",
        uiSref: "edit",
        state: {
          url: "/edit",
          views: {
            "tabs@main.contract.type.info": {
              component: configurableGridFormViewComponentName,
            },
          },
          resolve: {
            selectedIds: [
              RecordGridViewServiceName,
              ServerUtilityName,
              "contract",
              (
                recordGridViewService: RecordGridViewService,
                serverUtility: ServerUtility,
                contract: Record,
              ) => {
                const contractMachineConfig =
                  recordGridViewService.getGridConfiguration(
                    config.contractMachineObjectName,
                  )!;

                return serverUtility

                  .fetchAll<Record>({
                    url: contractMachineConfig.gridDataSource!.url,
                    query: {
                      ContractInstanceId: contract.ContractInstanceId,
                    },
                  })
                  .then((result) => result.map((r) => r.MachineId));
              },
            ],
            gridConfiguration: [
              RecordGridViewServiceName,
              GridSettingsServiceName,
              (
                recordGridViewService: RecordGridViewService,
                gridSettingsService: any,
              ) => {
                const gridConfig = recordGridViewService.getGridConfiguration(
                  config.machineName,
                )!;
                gridConfig.gridSettings = gridSettingsService.getGridSettings(
                  Symbol(config.gridEditSymbolKey),
                );
                return gridConfig;
              },
            ],
            config: [
              ServerUtilityName,
              ConfigurableGridFormViewServiceName,
              "contract",
              "gridConfiguration",
              "selectedIds",
              (
                serverUtility: ServerUtility,
                configurableGridFormViewService: ConfigurableGridFormViewService,
                contract: Record,
                gridConfiguration: GridConfiguration,
                selectedIds: number[],
              ) =>
                getConfigurationGridEditViewConfigForContractMachines({
                  configurableGridFormViewService,
                  gridConfiguration,
                  selectedIds,
                  gridSelectionChanged(args: GridSelectionChangedArgs) {
                    const ContractInstanceId =
                      contract.ContractInstanceId as number;
                    const machineId = args.data.Id;
                    setContractMachine(
                      serverUtility,
                      machineId,
                      ContractInstanceId,
                      args.selected,
                    );
                  },
                }),
            ],
          },
        },
      },
    ],
  };
}

contractMachineConfig.$inject = [
  RecordGridViewServiceProviderName,
  ColumnDefinitionSetServiceProviderName,
];

export function contractMachineConfig(contractObjectName: string) {
  return [
    RecordGridViewServiceProviderName,
    ColumnDefinitionSetServiceProviderName,
    (
      recordGridViewServiceProvider: RecordGridViewServiceProvider,
      columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider,
    ) => {
      columnDefinitionSetServiceProvider.addColumnDefinitionSet(
        vsMachineColumnSetName,
        [
          ColumnDefinitionSetServiceName,
          (columnDefinitionSetService: ColumnDefinitionSetService) =>
            columnDefinitionSetService.getColumnDefinitionSet(machineName) ??
            [],
        ],
      );

      columnDefinitionSetServiceProvider.addColumnDefinitionSet(
        vsContractColumnSetName,
        [
          ColumnDefinitionSetServiceName,
          (columnDefinitionSetService: ColumnDefinitionSetService) =>
            columnDefinitionSetService.getColumnDefinitionSet(
              contractObjectName,
            ) ?? [],
        ],
      );

      columnDefinitionSetServiceProvider.addColumnDefinitionSet(vsObjectName, [
        ColumnDefinitionSetServiceName,
        (columnDefinitionSetService: ColumnDefinitionSetService) => [
          ...(columnDefinitionSetService.getColumnDefinitionSet(
            contractObjectName,
          ) ?? []),
          ...(columnDefinitionSetService.getColumnDefinitionSet(machineName) ??
            []),
        ],
      ]);

      recordGridViewServiceProvider.addGridConfiguration(vsObjectName, [
        ServerUtilityName,
        GridSettingsServiceName,
        (
          serverUtility: ServerUtility,
          gridSettingsService: GridSettingsService,
        ): GridConfiguration => {
          return {
            defaultSort: "SerialNumber,ContractNumber",
            gridDataSource: new RecordDataSource(
              serverUtility,
              "/api/contract/machines",
            ),
            gridSettings: gridSettingsService.getGridSettings(
              Symbol(vsObjectName),
            ),
            namePlural: "Contract Machines",
            nameSingular: "Contract Machine",
            showAddButton: false,
            columnDefinitionSet: vsObjectName,
          };
        },
      ]);
    },
  ];
}

function setContractMachine(
  serverUtility: ServerUtility,
  machineId: number,
  ContractInstanceId: number,
  setSelection: boolean,
) {
  const url = "/api/contract/machines";

  setSelection
    ? serverUtility.post({
        url,
        postObject: {
          ContractInstanceId: ContractInstanceId,
          MachineId: machineId,
        },
      })
    : serverUtility.sendApiRequest<ServiceResponse<Record>>({
        url: url + `/${ContractInstanceId}/byMachineId`,
        method: "delete",
        data: {
          MachineId: machineId,
        },
      });
}

function getConfigurationGridEditViewConfigForContractMachines(config: {
  configurableGridFormViewService: ConfigurableGridFormViewService;
  selectedIds: number[];
  gridConfiguration: GridConfiguration;
  gridSelectionChanged: (args: GridSelectionChangedArgs) => void;
}): ConfigurableGridFormViewConfig {
  const {
    selectedIds,
    gridConfiguration,
    gridSelectionChanged,
    configurableGridFormViewService,
  } = config;

  return {
    defaultQuery: {
      Ids: selectedIds.length > 0 ? selectedIds : [0],
    },
    gridConfiguration,
    additionalToggleButtons: [
      configurableGridFormViewService.idDefaultQueryToggleButton(true),
    ],
    showNavButton: true,
    navButtonIcon: "fa-pencil",
    navButtonPermission: undefined,
    navButtonText: "Done",
    navPath: "^",
    selectedIds,
    showSelectButtons: true,
    gridSelectionChanged: (args: GridSelectionChangedArgs) => {
      gridSelectionChanged(args);
    },
  };
}
function getConfigurableGridFormViewConfigForContractMachines(
  recordGridViewService: RecordGridViewService,
  gridSettings: IGridSettings,
  defaultQuery: IQuery,
  columns: ColDef[],
  columnDefinitionService: ColumnDefinitionService,
): ConfigurableGridFormViewConfig {
  const contractMachineGridConfiguration =
    recordGridViewService.getGridConfiguration(vsObjectName)!;

  contractMachineGridConfiguration.gridSettings = gridSettings;
  const infoColumn: ColDef = columnDefinitionService.getLinkColDef({
    colId: "info",
    headerName: "Info",
    iconClass: "fa fa-info-circle text-primary",
    stateExpression: `main.machines.detail.configurations({machineId: data.MachineId})`,
  });

  const ColumnsWithInfo = [...columns, infoColumn];
  contractMachineGridConfiguration.columns = ColumnsWithInfo;

  return {
    defaultQuery,
    gridConfiguration: contractMachineGridConfiguration,
    additionalToggleButtons: [],
    navButtonIcon: "fa-pencil",
    navButtonPermission: "contractEdit",
    navButtonText: "Edit",
    navPath: ".edit",
    showNavButton: true,
    showSelectButtons: false,
  };
}
